import type { ReactNode } from 'react';
import {
    BottomNavigation,
    BottomNavigationAction,
    Paper,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Dashboard as DashboardIcon,
    Campaign as CampaignIcon,
    Tv as ScreenIcon,
    BookOnline as BookingIcon,
    BarChart as AnalyticsIcon,
    Explore as ExploreIcon,
    Payments as PayoutsIcon,
    PermMedia as MediaIcon,
    MoreHoriz as MoreIcon,
} from '@mui/icons-material';
import { useUserRole } from '../../hooks/useUserRole';
import { useAccountVisibility } from '../../hooks/useAccountVisibility';
import { getMobileNavigation } from '../../constants/roleRouteMatrix';
import type { NavIconKey } from '../../constants/roleRouteMatrix';

export default function MobileBottomNav({ onMore }: { onMore?: () => void }) {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { role } = useUserRole();
    const { isPrivate } = useAccountVisibility();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { t } = useTranslation();
    const iconMap: Partial<Record<NavIconKey, ReactNode>> = {
        dashboard: <DashboardIcon />,
        campaigns: <CampaignIcon />,
        screens: <ScreenIcon />,
        bookings: <BookingIcon />,
        analytics: <AnalyticsIcon />,
        discover: <ExploreIcon />,
        payouts: <PayoutsIcon />,
        media: <MediaIcon />,
        more: <MoreIcon />,
    };

    const filteredItems = getMobileNavigation({ role, isPrivate }).map((item) => ({
        ...item,
        label: item.labelKey ? t(item.labelKey, item.text) : item.text,
        icon: iconMap[item.iconKey] ?? <DashboardIcon />,
    }));

    // Selected tab is purely derived from the current path — compute it during
    // render instead of syncing it into state via an effect.
    const derivedIndex = filteredItems.findIndex(item => item.path !== '#more' && location.pathname.startsWith(item.path));
    // Nothing on the bar matches a More page (Creatives, Results, Settings), so More lights up instead
    const moreIndex = filteredItems.findIndex(item => item.path === '#more');
    const value = derivedIndex !== -1 ? derivedIndex : moreIndex !== -1 ? moreIndex : 0;

    // Only show on mobile devices
    if (!isMobile) {
        return null;
    }

    return (
        <Paper
            sx={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: theme.zIndex.appBar,
                borderRadius: 0,
                borderWidth: '1px 0 0 0',
            }}
            elevation={0}
        >
            <BottomNavigation
                // Five actions at MUI's 80px minimum would be wider than a 390px phone; share the width instead.
                sx={{ bgcolor: 'transparent', '& .MuiBottomNavigationAction-root': { minWidth: 0, px: 0.5 } }}
                value={value}
                onChange={(_event, newValue) => {
                    const target = filteredItems[newValue];
                    if (target.path === '#more') onMore?.();
                    else navigate(target.path);
                }}
                showLabels
            >
                {filteredItems.map((item) => (
                    <BottomNavigationAction
                        key={item.path}
                        label={item.label}
                        icon={item.icon}
                    />
                ))}
            </BottomNavigation>
        </Paper>
    );
}
